import React, { useState } from 'react';
import styled from 'styled-components';
import { images } from '../../../assets/images';
import { isValidEmail } from '../../../utils/password-policy';
import {
  AuthCard,
  AuthInner,
  AuthLogo,
  AuthFooterRow,
  AuthLink,
  AuthInput,
  AuthSubmitButton,
  Divider,
  ErrorBanner,
  SocialButton,
  TermsNotice,
} from './AuthUi';

export interface LoginScreenProps {
  onLogin: (email: string, password: string) => void;
  onSocial: (connection: 'google-oauth2' | 'apple') => void;
  onForgotPassword: () => void;
  onSignup: () => void;
  loading: boolean;
  errorMessage?: string | null;
}

export const LoginScreen = (props: LoginScreenProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);

  const emailError = touched && !isValidEmail(email) ? 'Enter a valid email.' : null;
  const passwordError = touched && !password ? 'Enter your password.' : null;

  const submit = () => {
    setTouched(true);
    if (!isValidEmail(email) || !password) return;
    props.onLogin(email.trim(), password);
  };

  return (
    <AuthCard>
      <AuthInner>
        <AuthLogo src={images.risenHopeLogo} alt="Risen Hope" />

        <AuthInput
          label="Email"
          value={email}
          onChange={setEmail}
          placeholder="you@example.com"
          type="email"
          error={emailError}
        />
        <Spacer />
        <AuthInput
          label="Password"
          value={password}
          onChange={setPassword}
          placeholder="Your password"
          type="password"
          error={passwordError}
          onKeyDown={e => {
            if (e.key === 'Enter') submit();
          }}
        />
        <ForgotRow>
          <ForgotBtn type="button" onClick={props.onForgotPassword}>
            Forgot password?
          </ForgotBtn>
        </ForgotRow>

        {!!props.errorMessage && (
          <>
            <Spacer />
            <ErrorBanner message={props.errorMessage} />
          </>
        )}

        <Spacer $size={20} />
        <AuthSubmitButton title="Log in" onClick={submit} loading={props.loading} />
        <Spacer $size={20} />
        <Divider label="or" />
        <Spacer $size={20} />

        <TermsNotice prefix="By continuing" />
        <Spacer $size={12} />

        <SocialButton
          variant="google"
          title="Continue with Google"
          onPress={() => props.onSocial('google-oauth2')}
          disabled={props.loading}
        />
        <Spacer $size={12} />
        <SocialButton
          variant="apple"
          title="Continue with Apple"
          onPress={() => props.onSocial('apple')}
          disabled={props.loading}
        />

        <AuthFooterRow>
          <span>Don't have an account?</span>
          <AuthLink onClick={props.onSignup}>Sign up</AuthLink>
        </AuthFooterRow>
      </AuthInner>
    </AuthCard>
  );
};

const Spacer = styled.div<{ $size?: number }>`
  height: ${p => p.$size ?? 16}px;
`;

const ForgotRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;

const ForgotBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: Nunito, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #0038b8;
`;
